import { parseSummaryJsonToHtml, formatClinicalDate } from "./jsonToHtmlParser";

/**
 * Extracts a value from a nested object using a dot-notated path.
 */
const getNestedValue = (obj, path) => {
  if (!obj) return null;
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined) ? acc[part] : null, obj);
};

/**
 * Formats a raw value into a string suitable for HTML insertion.
 */
const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const valid = value.filter(v => v !== null && v !== undefined && v !== "");
    return valid.length ? valid.join(", ") : "";
  }
  if (typeof value === "object") {
    // If it's a medication object or similar
    if (value.name || value.drug_name) {
      const name = value.name || value.drug_name;
      const dose = value.dose ? ` • ${value.dose}` : '';
      const freq = value.frequency ? ` • ${value.frequency}` : '';
      const period = value.period ? ` • ${value.period} ${value.periodUnit || 'days'}` : '';
      return `${name}${dose}${freq}${period}`;
    }
    // Fallback for other objects
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  // ISO date / datetime strings → readable date (e.g. "2026-06-03T00:00:00" → "03 Jun 2026").
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}([T\s]|$)/.test(value.trim())) {
    return formatClinicalDate(value);
  }
  return String(value);
};

/**
 * Hydrates a raw HTML template string with data from the summaryData object.
 * If the template doesn't provide HTML, it falls back to the default HTML generator.
 * 
 * @param {string} templateHtml - The raw HTML template from the bucket.
 * @param {object} summaryData - The live data model for the admission.
 * @returns {string} The fully hydrated HTML string for TipTap.
 */
export const hydrateTemplate = (templateHtml, summaryData) => {
  // If no HTML is provided by the backend template, fallback to our default HTML builder
  if (!templateHtml || typeof templateHtml !== 'string' || templateHtml.trim() === '') {
    return parseSummaryJsonToHtml(summaryData);
  }

  // 1. First, handle mustache-style {{ variable }} tags (for backwards compatibility)
  const variableRegex = /\{\{\s*([\w.]+)\s*\}\}/g;
  
  const keyMap = {
    'client_name': 'header.name',
    'patient_name': 'header.name',
    'patient_id': 'header.patient_id',
    'invoice_no': 'header.invoice_no',
    'date_of_admission': 'header.date_of_admission',
    'date_of_discharge': 'header.date_of_discharge',
    'age': 'header.age',
    'gender': 'header.gender',
    
    // Legacy / New Template mapping
    'hospital_no': 'header.patient_id',
    'age_sex': 'custom_age_sex', 
    'ip_no': 'header.admission_id',
    'admission_date': 'header.date_of_admission',
    'discharge_date': 'header.date_of_discharge',
    'admitted_under': 'header.consultant_incharge',
    'department': 'header.department',
    'payor': 'header.category',
    'ward_bed': 'header.bed_no',
    
    'treating_clinicians': 'header.consultant_incharge',
    'diagnosis': 'final_diagnosis',
    'chief_complaints': 'history.presenting_complaints',
    'history_current_illness': 'history.history_of_present_illness',
    'past_history': 'history.past_medical_history',
    
    'gpe_pallor': 'header.discharge_details.vital_signs.pallor',
    'gpe_icterus': 'header.discharge_details.vital_signs.icterus',
    'gpe_edema': 'header.discharge_details.vital_signs.edema',
    'gpe_clubbing': 'header.discharge_details.vital_signs.clubbing',
    'gpe_cyanosis': 'header.discharge_details.vital_signs.cyanosis',
    'gpe_lymphadenopathy': 'header.discharge_details.vital_signs.lymphadenopathy',

    // Vital signs at discharge, filled from the last nurse-recorded set. Without
    // these aliases a template could only reach them through the full dotted
    // path, so the values sat in the payload and printed nowhere.
    'bp': 'header.discharge_details.vital_signs.bp',
    'blood_pressure': 'header.discharge_details.vital_signs.bp',
    'pulse': 'header.discharge_details.vital_signs.pulse',
    'heart_rate': 'header.discharge_details.vital_signs.pulse',
    'temperature': 'header.discharge_details.vital_signs.temperature',
    'spo2': 'header.discharge_details.vital_signs.spo2',
    'respiratory_rate': 'header.discharge_details.vital_signs.respiratory_rate',
    'consciousness': 'header.discharge_details.vital_signs.consciousness',
    'vitals_recorded_at': 'header.discharge_details.vital_signs.recorded_at',
    
    'physical_examination': 'treatment_given.procedures',
    'lab_investigations': 'investigations',
    'course_of_treatment': 'header.discharge_details.remarks',
    'condition_at_discharge': 'header.discharge_details.condition_at_discharge',
    
    // Medication rows mapping
    'med_1': 'advices.medications_to_continue.0',
    'med_2': 'advices.medications_to_continue.1',
    'med_3': 'advices.medications_to_continue.2',
    'med_4': 'advices.medications_to_continue.3',
    'med_5': 'advices.medications_to_continue.4',
    'med_6': 'advices.medications_to_continue.5',
    
    // Footer fields
    'doctor_name': 'header.consultant_incharge',
    'appointment_contact': 'hospital_details.contact',
    'emergency_contact': 'hospital_details.contact',

    // Death summary (MCCD) — populated only for deceased patients, injected as
    // `death.*` by DischargeSummaryModal.buildDeathHydration(). A death-summary
    // template can use any of these placeholders; they render empty for a living
    // patient. (Unaliased `{{death.<field>}}` also works via the direct-path
    // fallback below.)
    'cause_of_death': 'death.cause_text',
    'underlying_cause': 'death.underlying',
    'manner_of_death': 'death.manner',
    'certifying_doctor': 'death.certifying_doctor',
    'certifying_doctor_regno': 'death.certifying_doctor_regno',
    'certifying_doctor_quals': 'death.certifying_doctor_quals',
    'time_of_death': 'death.time_of_death',
    'cod_1a': 'death.cod_1a',
    'cod_1a_interval': 'death.cod_1a_interval',
    'cod_1b': 'death.cod_1b',
    'cod_1b_interval': 'death.cod_1b_interval',
    'cod_1c': 'death.cod_1c',
    'cod_1c_interval': 'death.cod_1c_interval',
    'cod_part2': 'death.part2',
    'mccd_number': 'death.mccd_number',
  };

  const getMappedValue = (field) => {
    const resolvedPath = keyMap[field] || field;
    if (resolvedPath === 'custom_age_sex') {
      const age = getNestedValue(summaryData, 'header.age');
      const sex = getNestedValue(summaryData, 'header.gender');
      if (age || sex) {
        return `${age || ''} / ${sex || ''}`.replace(/^ \/ | \/ $/g, '');
      }
      return null;
    }
    return getNestedValue(summaryData, resolvedPath);
  };

  let html = templateHtml.replace(variableRegex, (match, path) => {
    const val = getMappedValue(path);
    if (val !== null && val !== undefined) {
      const displayStr = formatDisplayValue(val);
      const resolvedPath = keyMap[path] || path;
      return `<span class="auto-hydrated" data-field="${resolvedPath}" title="Auto-filled from: ${resolvedPath}">${displayStr}</span>`;
    }
    return '';
  });

  // 2. Second, handle elements with data-field attribute (for the new standalone HTML template structure)
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const elements = doc.querySelectorAll('[data-field]');
    
    elements.forEach(el => {
      const field = el.getAttribute('data-field');
      const val = getMappedValue(field);
      
      if (val !== null && val !== undefined) {
        const displayStr = formatDisplayValue(val);
        if (displayStr) {
          el.innerHTML = displayStr;
          el.classList.add('auto-hydrated');
        }
      }
    });
    
    // Tiptap handles full HTML strings fine (it extracts the body), 
    // but to be safe we return the full outerHTML so any inline styles/wrappers are preserved.
    return doc.documentElement.outerHTML;
  } catch (err) {
    console.error("Error hydrating data-field elements", err);
    return html;
  }
};

